import { useMemo } from "react";
import { SpaceRatioValidation } from "@/services/statistical-analysis-service";
import { BaseAlertState } from "../base/BaseAlert.types";
import { useAlertState } from "../base/useAlertState";

export interface UseCapacityAlertStateParams {
  spaceRatioValidation?: SpaceRatioValidation;
  localSizeCapacityRatio?: {
    ratio: number;
    isWithinStandard: boolean;
    status: string;
    warning: string | null;
  } | null;
  loading?: boolean;
}

export const useCapacityAlertState = ({
  spaceRatioValidation,
  localSizeCapacityRatio,
  loading = false,
}: UseCapacityAlertStateParams): BaseAlertState | null => {
  const { baseState, capacitySpecificState } = useMemo(() => {
    const backendRatioData = spaceRatioValidation;
    const isLoading = !backendRatioData && !localSizeCapacityRatio && loading;

    if (isLoading) {
      return {
        baseState: {
          loading: true,
          loadingMessage: "Calculando proporção do espaço...",
        },
        capacitySpecificState: {
          type: "loading" as const,
          title: "Aguardando análise",
        },
      };
    }

    if (!spaceRatioValidation && !localSizeCapacityRatio && !loading) {
      return {
        baseState: { hasData: false },
        capacitySpecificState: null,
      };
    }

    const ratioValidation = backendRatioData || localSizeCapacityRatio;

    if (!ratioValidation) {
      return {
        baseState: { hasData: false },
        capacitySpecificState: null,
      };
    }

    const isWithinStandard = ratioValidation.isWithinStandard;

    if (isWithinStandard) {
      const message = `Boa proporção: cerca de ${ratioValidation.ratio.toFixed(
        1,
      )} m² por pessoa. O espaço está dentro do ideal para conforto e uso.`;

      return {
        baseState: {
          loading: false,
          hasData: true,
          successMessage: message,
        },
        capacitySpecificState: {
          type: "success" as const,
          title: "Proporção adequada",
        },
      };
    } else {
      const message =
        ratioValidation.warning ||
        `Proporção inadequada: cerca de ${ratioValidation.ratio.toFixed(
          1,
        )} m² por pessoa. Ajuste o tamanho ou a capacidade para garantir conforto dos clientes.`;

      const isHigh = ratioValidation.status === "below_minimum";
      const title = `Espaço ${isHigh ? "abaixo" : "acima"} do ideal`;

      return {
        baseState: {
          loading: false,
          hasData: true,
          hasError: true,
          errorMessage: message,
        },
        capacitySpecificState: {
          type: "error" as const,
          title: title,
        },
      };
    }
  }, [spaceRatioValidation, localSizeCapacityRatio, loading]);

  const alertState = useAlertState(baseState);

  return useMemo(() => {
    if (!alertState || !capacitySpecificState) return alertState;

    return {
      ...alertState,
      type: capacitySpecificState.type,
      title: capacitySpecificState.title,
    };
  }, [alertState, capacitySpecificState]);
};
